import type { PresenceStatus } from '@lagerfeuer/shared';
import { CustomStatusText } from '../profile/CustomStatusText';
import { activeCustomStatus } from '../profile/customStatus';
import { LogOut, Settings, Smile } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Avatar, STATUS_LABELS } from '../../components/Avatar';
import { Menu, anchorFrom, type Anchor } from '../../components/Popover';
import { Tooltip } from '../../components/Tooltip';
import { errorMessage } from '../../lib/api';
import { presenceApi } from '../../lib/endpoints';
import { paths } from '../../lib/routes';
import { gatewaySend } from '../../realtime/gateway';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { displayName } from '../../store/selectors';
import { toast } from '../../store/ui';
import { logout } from '../auth/session';
import { extraMenuItems } from '../extras';

const STATUSES: PresenceStatus[] = ['ONLINE', 'IDLE', 'DND', 'INVISIBLE'];

/** Eigenes Profil unten in der Seitenleiste: Status, eigener Status, Einstellungen. */
export function UserPanel() {
  const me = useAppStore((s) => s.me);
  const navigate = useNavigate();
  const [menu, setMenu] = useState<Anchor | null>(null);
  if (!me) return null;
  const setStatus = async (status: PresenceStatus) => {
    useAppStore.getState().update((d) => {
      if (d.me) d.me.status = status;
    });
    try {
      const res = await gatewaySend('PRESENCE_SET', { status });
      if (!res) await presenceApi.set(status);
    } catch (e) {
      toast.error('Status konnte nicht gesetzt werden', errorMessage(e));
    }
  };
  const extra = extraMenuItems.filter((i) => i.slot === 'userMenu');
  return (
    <div className="user-panel" data-testid="user-panel">
      <button className="user-panel-me" onClick={(e) => setMenu(anchorFrom(e.currentTarget))} aria-haspopup="menu" data-testid="user-panel-menu">
        <span className="avatar-with-status">
          <Avatar user={me} size={34} />
          <span className="status-dot" data-status={me.status === 'INVISIBLE' ? 'OFFLINE' : me.status} data-testid="my-status" data-value={me.status} />
        </span>
        <span className="user-panel-text">
          <span className="truncate user-panel-name">{displayName(me)}</span>
          <span className="truncate small faint" data-testid="user-panel-status">
            <CustomStatusText status={me.customStatus} fallback={STATUS_LABELS[me.status]} />
          </span>
        </span>
      </button>
      <Tooltip label="Einstellungen">
        <button className="icon-btn" onClick={() => navigate(paths.settings())} data-testid="open-settings">
          <Settings size={18} />
        </button>
      </Tooltip>
      {menu && (
        <Menu
          anchor={menu}
          placement="top-start"
          onClose={() => setMenu(null)}
          testId="status-menu"
          items={[
            ...STATUSES.map((st) => ({
              id: `status-${st.toLowerCase()}`,
              label: STATUS_LABELS[st],
              icon: <span className="status-dot inline" data-status={st === 'INVISIBLE' ? 'OFFLINE' : st} />,
              onSelect: () => void setStatus(st),
            })),
            { id: 'custom-status', label: activeCustomStatus(me.customStatus) ? 'Eigenen Status ändern' : 'Eigenen Status festlegen', icon: <Smile size={16} />, separatorBefore: true, onSelect: () => overlays.open({ type: 'customStatus' }) },
            ...extra.map((it) => ({ id: `extra-${it.id}`, label: it.label, onSelect: () => (it.to ? navigate(it.to) : overlays.open({ type: 'extra', item: it, ctx: {} })) })),
            { id: 'logout', label: 'Abmelden', icon: <LogOut size={16} />, danger: true, separatorBefore: true, onSelect: () => void logout().then(() => navigate('/login')) },
          ]}
        />
      )}
    </div>
  );
}
